<template>
    <section class="games-calendar">
        <PageHeader title="Календарь игр" tag="h2" stack-until="md">
            <CompetitionStageSelect :competition-types="competitionTypes" :stages="stages" :disabled="loading"
                @change="onCompetitionChange" />
            <TeamFilterSelect :model-value="selectedTeamId" :teams="teamsList" :disabled="loading"
                @change="onTeamChange" />
        </PageHeader>

        <div class="games-calendar__nav">
            <button type="button" class="games-calendar__nav-btn" aria-label="Предыдущий месяц" :disabled="loading"
                @click="shiftMonth(-1)">
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                    <path d="M12.5 4.5L7 10l5.5 5.5" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"
                        stroke-linejoin="round" />
                </svg>
            </button>
            <div class="games-calendar__period">
                <CalendarMonthPicker :year="calendarData.year" :month="calendarData.month" :label="calendarData.label"
                    :disabled="loading" @select="onMonthPick" />
                <p v-if="seasonLabel" class="games-calendar__season">{{ seasonLabel }}</p>
            </div>
            <button type="button" class="games-calendar__nav-btn" aria-label="Следующий месяц" :disabled="loading"
                @click="shiftMonth(1)">
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                    <path d="M7.5 4.5L13 10l-5.5 5.5" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"
                        stroke-linejoin="round" />
                </svg>
            </button>
        </div>

        <div class="games-calendar__today-row">
            <button
                type="button"
                class="games-calendar__today"
                :disabled="loading || isViewingToday"
                @click="goToday"
            >
                Сегодня
            </button>
        </div>

        <div class="games-calendar__body" :class="{ 'is-loading': loading }">
            <div class="games-calendar__desktop">
                <CalendarMonthGrid :year="calendarData.year" :month="calendarData.month"
                    :games-by-date="calendarData.gamesByDate" @select-game="onSelectGame" />
            </div>
            <div class="games-calendar__mobile">
                <CalendarMobileMonth :year="calendarData.year" :month="calendarData.month"
                    :games-by-date="calendarData.gamesByDate" :today-request="todayRequest"
                    @select-game="onSelectGame" @select-date="mobileSelectedDateKey = $event" />
            </div>
        </div>

        <UpcomingMatchModal :show="!!upcomingGame" :game="upcomingGame" @close="upcomingGame = null" />
    </section>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import axios from 'axios';
import PageHeader from '@/components/layout/PageHeader.vue';
import CompetitionStageSelect from '@/components/filters/CompetitionStageSelect.vue';
import TeamFilterSelect from '@/components/filters/TeamFilterSelect.vue';
import CalendarMonthGrid from './CalendarMonthGrid.vue';
import CalendarMobileMonth from './CalendarMobileMonth.vue';
import CalendarMonthPicker from './CalendarMonthPicker.vue';
import UpcomingMatchModal from './UpcomingMatchModal.vue';
import { getCompetitionStageGroups } from '@/utils/skillQualityConfig';
import { formatDateKey } from '@/utils/calendarMonth';

const props = defineProps({
    calendar: {
        type: Object,
        required: true,
    },
    teams: {
        type: Array,
        default: () => [],
    },
});

function getDefaultCompetitionSelection() {
    const groups = getCompetitionStageGroups();
    return {
        competitionTypes: groups.map((group) => group.value),
        stages: groups.flatMap((group) => group.stages.map((stage) => stage.value)),
    };
}

const defaults = getDefaultCompetitionSelection();

const calendarData = ref({
    year: props.calendar.year,
    month: props.calendar.month,
    label: props.calendar.label,
    gamesByDate: props.calendar.gamesByDate || {},
    teams: props.calendar.teams || props.teams || [],
    season: props.calendar.season || null,
    meta: props.calendar.meta || { hasPrev: true, hasNext: true },
});

const loading = ref(false);
const todayRequest = ref(0);
const mobileSelectedDateKey = ref(null);
const selectedTeamId = ref(null);
const competitionTypes = ref([...defaults.competitionTypes]);
const stages = ref([...defaults.stages]);
const upcomingGame = ref(null);

const teamsList = computed(() => calendarData.value.teams || props.teams || []);

const seasonLabel = computed(() => {
    const name = calendarData.value.season?.name;
    if (!name) {
        return '';
    }
    return /^сезон\b/i.test(name.trim()) ? name.trim() : `${name.trim()}`;
});

const competitionsParam = computed(() => buildCompetitionsParam(competitionTypes.value, stages.value));

const isViewingToday = computed(() => {
    const now = new Date();
    return calendarData.value.year === now.getFullYear()
        && calendarData.value.month === now.getMonth() + 1
        && mobileSelectedDateKey.value === formatDateKey(now);
});

function buildCompetitionsParam(types, selectedStages) {
    const result = [];
    const groups = getCompetitionStageGroups();

    groups.forEach((group) => {
        if (!types.includes(group.value)) {
            return;
        }

        if (!group.stages.length) {
            result.push(group.value);
            return;
        }

        group.stages.forEach((stage) => {
            if (selectedStages.includes(stage.value)) {
                result.push(stage.value);
            }
        });
    });

    return result;
}

watch(
    () => props.calendar,
    (next) => {
        if (!next) return;
        calendarData.value = {
            year: next.year,
            month: next.month,
            label: next.label,
            gamesByDate: next.gamesByDate || {},
            teams: next.teams || props.teams || [],
            season: next.season || null,
            meta: next.meta || { hasPrev: true, hasNext: true },
        };
    },
    { deep: true },
);

function onCompetitionChange({ competitionTypes: nextTypes, stages: nextStages }) {
    competitionTypes.value = nextTypes;
    stages.value = nextStages;
    loadCalendar({
        year: calendarData.value.year,
        month: calendarData.value.month,
    });
}

function onTeamChange(teamId) {
    selectedTeamId.value = teamId;
    loadCalendar({
        year: calendarData.value.year,
        month: calendarData.value.month,
    });
}

function shiftMonth(delta) {
    let year = calendarData.value.year;
    let month = calendarData.value.month + delta;

    if (month < 1) {
        month = 12;
        year -= 1;
    } else if (month > 12) {
        month = 1;
        year += 1;
    }

    loadCalendar({ year, month });
}

function onMonthPick({ year, month }) {
    loadCalendar({ year, month });
}

async function goToday() {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;

    if (calendarData.value.year !== year || calendarData.value.month !== month) {
        await loadCalendar({ year, month });
    }

    todayRequest.value += 1;
}

function onSelectGame(game) {
    if (!game || game.is_finished) {
        return;
    }
    upcomingGame.value = game;
}

async function loadCalendar({ year, month } = {}) {
    if (!year || !month) {
        return;
    }

    loading.value = true;
    try {
        const params = {
            year,
            month,
            competitions: competitionsParam.value,
        };

        if (selectedTeamId.value) {
            params.team_id = selectedTeamId.value;
        }

        const { data } = await axios.get('/api/calendar-games', { params });
        calendarData.value = {
            year: data.year,
            month: data.month,
            label: data.label,
            gamesByDate: data.gamesByDate || {},
            teams: data.teams || [],
            season: data.season || null,
            meta: data.meta || { hasPrev: true, hasNext: true },
        };

        if (selectedTeamId.value) {
            const stillExists = (data.teams || []).some((team) => team.id === selectedTeamId.value);
            if (!stillExists) {
                selectedTeamId.value = null;
            }
        }
    } catch (error) {
        console.error('Failed to load calendar games', error);
    } finally {
        loading.value = false;
    }
}
</script>

<style lang="scss" scoped>
@import '../../../../scss/tokens';

.games-calendar {
    margin-bottom: 2rem;
}

.games-calendar__period {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.games-calendar__season {
    margin: 0.15rem 0 0;
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1.2;
    color: $color-text-secondary;
    text-align: center;
}

.games-calendar__nav {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    margin: 0;
}

.games-calendar__today-row {
    display: flex;
    justify-content: center;
    margin: 0.35rem 0 1rem;
}

.games-calendar__today {
    margin: 0;
    padding: 0.15rem 0.65rem;
    border: 1px solid $color-brand-border;
    border-radius: $radius-pill;
    background: #fff;
    color: $color-brand;
    font: inherit;
    font-size: 0.75rem;
    font-weight: 600;
    line-height: 1.2;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: $color-brand-alpha-soft;
    }

    &:focus-visible {
        outline: 2px solid $color-brand;
        outline-offset: 2px;
    }

    &:disabled {
        opacity: 0.45;
        cursor: default;
    }
}

.games-calendar__nav-btn {
    width: 2.25rem;
    height: 2.25rem;
    border-radius: $radius-sm;
    border: 1px solid $color-brand-border;
    background: #fff;
    color: $color-brand;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease;

    &:hover:not(:disabled) {
        background: $color-brand-alpha-soft;
        border-color: $color-brand;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
}

.games-calendar__body {
    position: relative;

    &.is-loading {
        opacity: 0.72;
        pointer-events: none;
    }
}

.games-calendar__desktop {
    display: none;
}

@include up($bp-md) {
    .games-calendar__desktop {
        display: block;
    }

    .games-calendar__mobile {
        display: none;
    }
}
</style>
